import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SocketEvents, type RideLocationBroadcast, type RideStatus } from '@tcrd/shared';
import { api } from '../../lib/api';
import { realtime } from '../../lib/realtime';
import type { MapEngine } from '../../components/map/engine';
import { useCarAnimation } from '../../components/map/useCarAnimation';

export interface TrackSnapshot {
  rideId: string;
  status: RideStatus;
  live: boolean;
  gpsIntervalSeconds: number;
  noShowWaitMinutes?: number;
  staleSeconds: number;
  last: { lat: number; lng: number; heading: number | null; speed: number | null; accuracy: number | null; recordedAt: string; stale: boolean } | null;
  navigation: { target: 'PICKUP' | 'DESTINATION' | null; polyline: string | null; distanceMeters: number | null; durationSeconds: number | null } | null;
  remainingMeters: number | null;
  etaSeconds: number | null;
  trail: Array<{ lat: number; lng: number }>;
}

/**
 * Estado en vivo de un viaje: carga inicial, sala Socket.IO (re-unión tras
 * reconectar), posiciones animadas y detección de datos desactualizados.
 */
export function useLiveRide(rideId: string, engine: MapEngine | null, opts: { follow?: boolean } = {}) {
  const qc = useQueryClient();
  // Respaldo del socket: si la conexión en vivo se cae (pestaña en segundo plano en iPhone), la consulta trae la última posición.
  const [fastPoll, setFastPoll] = useState(false);
  const snapshot = useQuery({ queryKey: ['ride-track', rideId], queryFn: () => api<TrackSnapshot>(`/rides/${rideId}/track`), refetchInterval: fastPoll ? 10_000 : 30_000, refetchOnWindowFocus: true });
  const car = useCarAnimation(engine, opts);
  const [lastAt, setLastAt] = useState<number | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [eta, setEta] = useState<number | null>(null);
  const [stale, setStale] = useState(false);
  const [connected, setConnected] = useState(realtime.connected);
  const staleSec = snapshot.data?.staleSeconds ?? 30;
  const lastAtRef = useRef<number | null>(null);

  // Posición inicial desde la API (última conocida).
  useEffect(() => {
    const s = snapshot.data;
    if (!s || !engine) return;
    setRemaining(s.remainingMeters);
    setEta(s.etaSeconds);
    engine.setTrail(s.trail.length > 1 ? s.trail : null);
    const at = s.last ? new Date(s.last.recordedAt).getTime() : null;
    // Solo si es más reciente que lo recibido por el socket.
    if (s.last && at !== null && (lastAtRef.current === null || at > lastAtRef.current)) {
      lastAtRef.current = at;
      car.push({ lat: s.last.lat, lng: s.last.lng, heading: s.last.heading, at }, s.last.stale);
      setLastAt(at);
    }
    if (!s.live) car.clear();
  }, [snapshot.data, engine, car]);

  // Sala del viaje: se vuelve a unir en cada reconexión (el servidor revalida el permiso).
  const join = useCallback(() => void realtime.emit(SocketEvents.joinRide, { rideId }), [rideId]);
  useEffect(() => {
    join();
    const offStatus = realtime.onStatus((c) => {
      setConnected(c);
      if (c) {
        join();
        void snapshot.refetch();
      }
    });
    const offLoc = realtime.on(SocketEvents.rideLocation, (raw) => {
      const p = raw as unknown as RideLocationBroadcast;
      if (p.rideId !== rideId) return;
      const at = new Date(p.recordedAt).getTime();
      if (lastAtRef.current !== null && at <= lastAtRef.current) return;
      lastAtRef.current = at;
      car.push({ lat: p.lat, lng: p.lng, heading: p.heading, at }, p.stale);
      setLastAt(at);
      setRemaining(p.remainingMeters);
      setEta(p.etaSeconds);
    });
    const offRide = realtime.on(SocketEvents.rideUpdated, (p) => {
      if (p.rideId !== rideId) return;
      void qc.invalidateQueries({ queryKey: ['ride', rideId] });
      void snapshot.refetch();
    });
    return () => {
      offStatus();
      offLoc();
      offRide();
      void realtime.emit(SocketEvents.leaveRide, { rideId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideId, join, car]);

  // Datos antiguos: el carrito se atenúa y se informa; nunca se mueve sin datos reales.
  useEffect(() => {
    const t = setInterval(() => {
      const isStale = lastAt !== null && Date.now() - lastAt > staleSec * 1000;
      setStale(isStale);
      car.setStale(isStale);
      setFastPoll(isStale || !realtime.connected);
    }, 1000);
    return () => clearInterval(t);
  }, [lastAt, staleSec, car]);

  return { snapshot, remaining, eta, lastAt, stale, connected, currentPosition: car.current };
}
